import React, { useEffect, useRef, useState } from 'react';
import type { ProductMedia } from '@noths/polaris-design-system-ooux-interfaces';

import { useCarouselNavigation } from '../../../hooks/useCarouselNavigation';
import type { ProductCardMediaNavigatePayload } from './types';
import * as styles from './ProductCardMedia.styles';

/**
 * How many of a product's images the card will carry.
 *
 * A listing renders 60 cards, so an unusually well-photographed product must not
 * quietly cost the page a dozen image requests. Five is what the design draws.
 */
export const MAX_IMAGES = 5;

/**
 * How long the second image has to have been on screen before a hover counts.
 *
 * A listing is 60 cards and a mouse crosses several of them on its way somewhere
 * else, so without a dwell a single sweep would report a look at every card it
 * passed over. Matches the analytics definition rather than anything visual: the
 * reveal itself is immediate.
 */
export const HOVER_DWELL_MS = 1000;

interface ProductCardMediaProps {
  /** The product's ordered media. Images are used in order; video is skipped. */
  media: ProductMedia[];
  /** Called when a tap target over an image is clicked. The card's product click. */
  onLinkClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  /** Called on mousedown on a tap target. The card's product mousedown. */
  onLinkMouseDown?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  /**
   * Called when the customer reaches another image, by swiping or by hovering.
   * The card adds the product. Omit it and no scroll listener is attached.
   */
  onNavigate?: (payload: Omit<ProductCardMediaNavigatePayload, 'product'>) => void;
  /** Load the first image up front, for a card above the fold. */
  priority: boolean;
  /** The product's URL, so a tap on the image opens the product. */
  url: string;
}

/**
 * The card's image area: the images, the swipe between them, and the dots.
 *
 * Rendered inside the card's media element, which is the positioning context the
 * dots and the hover image rely on.
 *
 * Video is skipped for now. The design marks a video's position in the dots with a
 * triangle, but `ProductMedia` carries no poster URL, so a video slide has no frame
 * to show until that contract grows one.
 */
export const ProductCardMedia = ({
  media,
  onLinkClick,
  onLinkMouseDown,
  onNavigate,
  priority,
  url,
}: ProductCardMediaProps) => {
  const images = media.filter(({ type }) => type === 'image').slice(0, MAX_IMAGES);

  /* Most products have one image, and one image is not a carousel: there is nothing
     to swipe to, nothing to hover to, and nothing to count with dots. */
  const isCarousel = images.length > 1;

  const [scrolledTo, setScrolledTo] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [hasBeenHovered, setHasBeenHovered] = useState(false);

  const trackRef = useRef<HTMLDivElement>(null);
  const hoverDwell = useRef<ReturnType<typeof setTimeout>>();
  /* A ref rather than state: nothing renders differently once the hover has been
     reported, and a re-render would be one per card in a grid. */
  const hasReportedHover = useRef(false);

  /* The same settled-movement reporting the carousel uses, so a swipe is one
     report however many scroll events it takes and a resize that fires a scroll
     without moving the track is not reported at all. Only the reporting half is
     wanted here: a card has no arrows, so `scrollNext`/`scrollPrev` go unused and
     every movement settles as a swipe. */
  useCarouselNavigation(
    trackRef,
    onNavigate && (({ direction }) => onNavigate({ direction, method: 'swipe' })),
  );

  /* A card scrolled out of a listing and unmounted mid-hover has not been looked
     at, so the pending report goes with it. */
  useEffect(() => () => clearTimeout(hoverDwell.current), []);

  const hasHoverImage = hasBeenHovered && isCarousel;
  /* While the second image is showing, the dots have to agree with it. Still needed
     even though the dots are hidden wherever there is no touchscreen: a touchscreen
     laptop shows them and hovers, so it is the one device that does both. */
  const onScreen = isHovered && hasHoverImage ? 1 : scrolledTo;

  if (images.length === 0) {
    return <span css={styles.imagePlaceholder} />;
  }

  /* Slides are exactly one track wide, so the index is arithmetic rather than an
     IntersectionObserver: 60 cards would otherwise mean 300 observed elements.
     React bails out when the index has not changed, so this needs no throttling. */
  const handleScroll = ({ currentTarget }: React.UIEvent<HTMLDivElement>) => {
    const { clientWidth, scrollLeft } = currentTarget;

    if (clientWidth > 0) {
      setScrolledTo(Math.round(scrollLeft / clientWidth));
    }
  };

  /* Touch swipes instead, and should never pay for a second image. Anything else
     that reports a pointer — a mouse, a pen, or a browser that says nothing at
     all — can hover, so it gets the reveal. */
  const handlePointerEnter = ({ pointerType }: React.PointerEvent<HTMLDivElement>) => {
    if (pointerType === 'touch') {
      return;
    }

    setHasBeenHovered(true);
    setIsHovered(true);

    if (onNavigate && !hasReportedHover.current) {
      /* The latch is only set once the timer has fired, so a second enter with no
         leave between the two would schedule a second dwell and report twice.
         React pairs enter with leave, but they pair per pointer, so a pen and a
         mouse hovering at once is one enter each. */
      clearTimeout(hoverDwell.current);

      hoverDwell.current = setTimeout(() => {
        hasReportedHover.current = true;
        onNavigate({ method: 'hover' });
      }, HOVER_DWELL_MS);
    }
  };

  /* Clears the dwell as well as hiding the reveal: a pointer that has left did not
     stay. Leaving and returning starts the wait again, which is what "shown for
     `HOVER_DWELL_MS`" means. */
  const handlePointerLeave = () => {
    clearTimeout(hoverDwell.current);
    setIsHovered(false);
  };

  /**
   * Everything that only makes sense for a set of images, kept off a card that has
   * one.
   *
   * A single image gets a plain container: no carousel or slide semantics to
   * announce a carousel of one to a screen reader, and none of the scrolling
   * handlers or styles. The styles are the part with teeth. `overflow-x: auto` makes
   * an element a scroll container whether or not its content overflows, and
   * `overscroll-behavior-x: contain` on a scroll container stops a pan chaining to
   * an ancestor, so a card with one image would take a touch on its image, refuse to
   * pass the gesture on, and have nothing of its own to scroll. The card could not
   * be swiped from its image inside a horizontally scrollable parent.
   */
  const carouselProps: React.ComponentProps<'div'> = isCarousel
    ? {
        'aria-label': 'Product images',
        'aria-roledescription': 'carousel',
        onPointerEnter: handlePointerEnter,
        onPointerLeave: handlePointerLeave,
        onScroll: handleScroll,
        /* Attached only where there is something to report, and only on a real
           carousel: a single image has no second image to reach, and its container
           is not a scroll container to begin with. */
        ...(onNavigate && { ref: trackRef }),
        role: 'group',
      }
    : {};

  const slideProps = (index: number): React.ComponentProps<'div'> =>
    isCarousel
      ? {
          'aria-label': `${index + 1} of ${images.length}`,
          'aria-roledescription': 'slide',
          role: 'group',
        }
      : {};

  return (
    <>
      <div
        {...carouselProps}
        css={isCarousel ? styles.track : styles.imageRow}
        data-testid="product-card-media-track"
      >
        {images.map(({ alt, url: imageUrl }, index) => (
          <div
            {...slideProps(index)}
            css={isCarousel ? styles.slide : styles.frame}
            key={`${imageUrl}-${index}`}
          >
            <img
              alt={alt}
              css={styles.image}
              loading={priority && index === 0 ? 'eager' : 'lazy'}
              src={imageUrl}
              /* React 18 only applies the lowercase attribute; the camelCase form
                 warns and is dropped. Spread so TypeScript allows it. */
              {...(priority && index === 0 ? { fetchpriority: 'high' } : {})}
            />
            {/* Empty and aria-hidden on purpose: a tap target over the image, not a
                link a screen reader should find. The card's one link is the title.
                Unfocusable, so nothing hidden from the tree can still be tabbed to. */}
            {/* eslint-disable-next-line jsx-a11y/anchor-has-content */}
            <a
              aria-hidden
              css={styles.slideLink}
              data-testid="product-card-media-link"
              href={url}
              onClick={onLinkClick}
              onMouseDown={onLinkMouseDown}
              tabIndex={-1}
            />
          </div>
        ))}
      </div>

      {hasHoverImage && (
        <img
          alt=""
          aria-hidden
          css={styles.hoverImage}
          data-testid="product-card-media-hover-image"
          data-visible={isHovered}
          src={images[1].url}
        />
      )}

      {isCarousel && (
        <span aria-hidden css={styles.dots} data-testid="product-card-media-dots">
          {images.map(({ url: imageUrl }, index) => (
            <span css={styles.dot} data-active={index === onScreen} key={`${imageUrl}-${index}`} />
          ))}
        </span>
      )}
    </>
  );
};

ProductCardMedia.displayName = 'ProductCardMedia';
